import type {PreloadableComponent} from '@github-ui/react-core/app-routing-types'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {UserContext} from '@github-ui/use-user'
import {type ReactNode, useMemo} from 'react'

import {type IssuesLayoutPayload, issuesLayoutRoute, layoutCurrentUser} from './issues-layout-route'

/**
 * Hands viewer data from the Issues layout query to hooks that would otherwise read the app payload,
 * which data-router routes do not have.
 */
export function IssuesLayoutContext({
  layoutPayload,
  children,
}: {
  layoutPayload: IssuesLayoutPayload | undefined
  children: ReactNode
}) {
  const currentUser = useMemo(() => layoutCurrentUser(layoutPayload), [layoutPayload])

  return <UserContext value={currentUser}>{children}</UserContext>
}

/**
 * Wraps a leaf route in `IssuesLayoutContext`. We wrap each page here instead of `IssuesLayout`, because
 * wrapping the shared layout in anything (even a context) makes saved views load their data too early.
 */
export function withIssuesLayoutContext(Component: PreloadableComponent): PreloadableComponent {
  const WithIssuesLayoutContext: PreloadableComponent = () => {
    const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')

    return (
      <IssuesLayoutContext layoutPayload={layoutPayload}>
        <Component />
      </IssuesLayoutContext>
    )
  }
  // Keep the page's loading settings, since the router now sees the wrapper instead of the page.
  WithIssuesLayoutContext.preload = Component.preload
  WithIssuesLayoutContext.ssr = Component.ssr
  return WithIssuesLayoutContext
}
